import React, { useCallback, useEffect, useState } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import { sortLicensesByState } from "../utils/licenses";
import useFocusTrap from "../hooks/useFocusTrap";

const API_BASE = process.env.REACT_APP_API_URL || "";

// Ensure we only pass a bucket-relative path to Supabase
const normalizeStoragePath = (value) => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const cleaned = raw.replace(/^\/+/, "");
  const withoutBucket = cleaned.replace(/^license_docs\//i, "");
  if (withoutBucket !== cleaned) return withoutBucket;

  const storagePattern = /storage\/v1\/object\/(?:public|sign|auth)?\/?license_docs\/(.+)/i;

  if (/^https?:\/\//i.test(raw)) {
    try {
      const url = new URL(raw);
      const match = url.pathname.match(storagePattern);
      return match ? match[1] : "";
    } catch {
      return "";
    }
  }

  const match = cleaned.match(storagePattern);
  return match ? match[1] : cleaned;
};

export default function AdminDashboard() {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterStatus, setFilterStatus] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("Incorrect document");
  const [flagNotes, setFlagNotes] = useState("");
  const [flagLicenseId, setFlagLicenseId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLicenseId, setDeleteLicenseId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [adminId, setAdminId] = useState(null);
  const [excludeQuery, setExcludeQuery] = useState("");
  const [excludedProfiles, setExcludedProfiles] = useState([]);
  const [profileSearchResults, setProfileSearchResults] = useState([]);
  const [exclusionsLoading, setExclusionsLoading] = useState(false);
  const [exclusionsError, setExclusionsError] = useState("");
  const [excludeBusyId, setExcludeBusyId] = useState("");
  const [excludedModalOpen, setExcludedModalOpen] = useState(false);
  const [affiliateLoading, setAffiliateLoading] = useState(false);
  const [affiliateError, setAffiliateError] = useState("");
  const [affiliateApplications, setAffiliateApplications] = useState([]);
  const [activeAffiliates, setActiveAffiliates] = useState([]);
  const [deniedAffiliateApplications, setDeniedAffiliateApplications] = useState([]);
  const [selectedAffiliateId, setSelectedAffiliateId] = useState("");
  const [affiliateBusyId, setAffiliateBusyId] = useState("");
  const [affiliateDecisionNotes, setAffiliateDecisionNotes] = useState("");
  const [removeAffiliateModalOpen, setRemoveAffiliateModalOpen] = useState(false);
  const [removeAffiliateId, setRemoveAffiliateId] = useState("");
  const flagModalRef = React.useRef(null);
  const deleteModalRef = React.useRef(null);
  const excludedModalRef = React.useRef(null);
  const removeAffiliateModalRef = React.useRef(null);

  const getAccessToken = useCallback(async () => {
    const { data: sessionData } = await supabase.auth.getSession();
    return sessionData?.session?.access_token || "";
  }, []);

  const adminApi = useCallback(async (path, { method = "GET", body } = {}) => {
    const accessToken = await getAccessToken();
    if (!accessToken) throw new Error("Not authorized");

    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });

    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(json?.error || "Request failed");
    }
    return json;
  }, [getAccessToken]);

  const formatProfileName = (profile) => {
    const first = String(profile?.first_name || "").trim();
    const last = String(profile?.last_name || "").trim();
    const full = `${first} ${last}`.trim();
    return full || "Unnamed profile";
  };

  const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const formatCurrency = (value) => {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return "$0.00";
    return amount.toLocaleString(undefined, { style: "currency", currency: "USD" });
  };

  const fetchAffiliates = useCallback(async () => {
    setAffiliateLoading(true);
    setAffiliateError("");
    try {
      const data = await adminApi("/api/admin/affiliates");
      const pending = Array.isArray(data?.applications) ? data.applications : [];
      const active = Array.isArray(data?.activeAffiliates) ? data.activeAffiliates : [];
      const denied = Array.isArray(data?.deniedApplications) ? data.deniedApplications : [];
      setAffiliateApplications(pending);
      setActiveAffiliates(active);
      setDeniedAffiliateApplications(denied);
      setSelectedAffiliateId((current) => current || active[0]?.id || pending[0]?.id || "");
    } catch (err) {
      setAffiliateError(err.message || "Unable to load affiliates.");
    } finally {
      setAffiliateLoading(false);
    }
  }, [adminApi]);

  const fetchLicenses = async (adminId) => {
    setLoading(true);
    setError("");
    // Fetch licenses and related profile info
    const { data: licenseRows, error: licErr } = await supabase
      .from("licenses")
      .select("*")
      .order("state", { ascending: true })
      .order("industry", { ascending: true })
      .order("category", { ascending: true });
    if (licErr) {
      setError(licErr.message);
      setLoading(false);
      return;
    }
    const userIds = [...new Set(licenseRows.map((l) => l.user_id))];
    const { data: profiles, error: profErr } = await supabase
      .from("profiles")
      .select("id,first_name,last_name,email,phone,company_agency")
      .in("id", userIds);
    if (profErr) {
      setError(profErr.message);
      setLoading(false);
      return;
    }
    const profileMap = Object.fromEntries((profiles || []).map((p) => [p.id, p]));
    const merged = (licenseRows || []).map((lic) => {
      const supportingDocument = lic.supporting_document || null;
      const normalizedDocPath = normalizeStoragePath(supportingDocument);
      const isHttp = /^https?:\/\//i.test(supportingDocument || "");
      const docUrl = isHttp && !normalizedDocPath ? supportingDocument : null;
      return {
        ...lic,
        docUrl,
        supporting_document: normalizedDocPath || null,
        profile: profileMap[lic.user_id] || {},
      };
    });
    setLicenses(sortLicensesByState(merged));
    setLoading(false);
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      const { data: sessionData } = await supabase.auth.getSession();
      const adminId = sessionData?.session?.user?.id;
      if (!adminId) {
        setError("Not authorized");
        setLoading(false);
        return;
      }
      setAdminId(adminId);
      // Check admin flag
      const { data: profile } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("id", adminId)
        .maybeSingle();
      if (!profile?.is_admin) {
        setError("Not authorized");
        setLoading(false);
        return;
      }

      setExclusionsLoading(true);
      setExclusionsError("");
      try {
        const data = await adminApi("/api/admin/search-exclusions");
        setExcludedProfiles(Array.isArray(data?.profiles) ? data.profiles : []);
      } catch (err) {
        setExclusionsError(err.message || "Unable to load excluded profiles.");
      } finally {
        setExclusionsLoading(false);
      }

      await fetchAffiliates();
      await fetchLicenses(adminId);
    };
    load();
  }, [adminApi, fetchAffiliates]);

  useEffect(() => {
    if (!adminId) return;
    const channel = supabase
      .channel("admin-licenses")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "licenses" },
        () => {
          fetchLicenses(adminId);
        }
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [adminId]);

  const updateStatus = async (licenseId, status, verified) => {
    const { error: updateErr } = await supabase
      .from("licenses")
      .update({ status, verified })
      .eq("id", licenseId);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    // optimistic update; realtime will also refresh
    setLicenses((prev) => sortLicensesByState(prev.map((l) => (l.id === licenseId ? { ...l, status, verified } : l))));
  };

  const handleFlag = (licenseId) => {
    setFlagLicenseId(licenseId);
    setFlagModalOpen(true);
  };

  const submitFlag = async () => {
    if (!flagLicenseId) return;
    const reasonText = flagReason === "Other" ? flagNotes.trim() : flagReason;
    setFlagModalOpen(false);
    setFlagNotes("");
    setFlagReason("Incorrect document");
    const status = "issues";
    await updateStatus(flagLicenseId, status, false);
    if (adminId) {
      await supabase.from("license_reviews").insert([
        {
          license_id: flagLicenseId,
          reviewer: adminId,
          decision: "rejected",
          notes: reasonText || "Flagged by admin",
        },
      ]);
    }
    setFlagLicenseId(null);
  };

  const openDeleteModal = (licenseId) => {
    setDeleteLicenseId(licenseId);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteBusy) return;
    setDeleteModalOpen(false);
    setDeleteLicenseId(null);
  };

  const submitDelete = async () => {
    if (!deleteLicenseId || deleteBusy) return;
    setDeleteBusy(true);
    setError("");
    try {
      await adminApi(`/api/admin/licenses/${deleteLicenseId}`, { method: "DELETE" });
      setLicenses((prev) => sortLicensesByState(prev.filter((lic) => lic.id !== deleteLicenseId)));
      setDeleteModalOpen(false);
      setDeleteLicenseId(null);
    } catch (err) {
      setError(err.message || "Unable to delete license.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleSearchProfiles = async (e) => {
    e.preventDefault();
    const term = excludeQuery.trim();
    if (!term) {
      setProfileSearchResults([]);
      setExclusionsError("");
      return;
    }
    setExclusionsLoading(true);
    setExclusionsError("");
    try {
      const data = await adminApi(`/api/admin/search-exclusions?q=${encodeURIComponent(term)}`);
      setProfileSearchResults(Array.isArray(data?.profiles) ? data.profiles : []);
    } catch (err) {
      setExclusionsError(err.message || "Unable to search profiles.");
    } finally {
      setExclusionsLoading(false);
    }
  };

  const updateExcludedStatus = async (profileId, exclude) => {
    if (!profileId) return;
    setExcludeBusyId(profileId);
    setExclusionsError("");
    try {
      const data = await adminApi(`/api/admin/search-exclusions/${profileId}`, {
        method: "PATCH",
        body: { exclude },
      });
      const updated = data?.profile;
      if (!updated?.id) return;

      setExcludedProfiles((prev) => {
        const without = prev.filter((p) => p.id !== updated.id);
        return exclude ? [updated, ...without] : without;
      });

      setProfileSearchResults((prev) =>
        prev.map((p) => (p.id === updated.id ? { ...p, exclude_from_search: updated.exclude_from_search } : p))
      );
    } catch (err) {
      setExclusionsError(err.message || "Unable to update exclusion.");
    } finally {
      setExcludeBusyId("");
    }
  };

  const updateAffiliateStatus = async (affiliateId, status) => {
    if (!affiliateId || affiliateBusyId) return;
    setAffiliateBusyId(affiliateId);
    setAffiliateError("");
    try {
      const data = await adminApi(`/api/admin/affiliates/${affiliateId}`, {
        method: "PATCH",
        body: { status, decisionNotes: affiliateDecisionNotes },
      });
      if (data?.emailWarning) setAffiliateError(data.emailWarning);
      setAffiliateDecisionNotes("");
      await fetchAffiliates();
      if (data?.affiliate?.id) setSelectedAffiliateId(data.affiliate.id);
    } catch (err) {
      setAffiliateError(err.message || "Unable to update affiliate.");
    } finally {
      setAffiliateBusyId("");
    }
  };

  const openRemoveAffiliateModal = (affiliateId) => {
    setRemoveAffiliateId(affiliateId);
    setRemoveAffiliateModalOpen(true);
  };

  const closeRemoveAffiliateModal = () => {
    if (affiliateBusyId) return;
    setRemoveAffiliateModalOpen(false);
    setRemoveAffiliateId("");
  };

  const submitRemoveAffiliate = async () => {
    if (!removeAffiliateId) return;
    await updateAffiliateStatus(removeAffiliateId, "removed");
    setRemoveAffiliateModalOpen(false);
    setRemoveAffiliateId("");
    setSelectedAffiliateId("");
  };

  useFocusTrap({
    isOpen: flagModalOpen,
    containerRef: flagModalRef,
    onClose: () => setFlagModalOpen(false),
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: deleteModalOpen,
    containerRef: deleteModalRef,
    onClose: closeDeleteModal,
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: excludedModalOpen,
    containerRef: excludedModalRef,
    onClose: () => setExcludedModalOpen(false),
    lockScroll: true,
  });

  useFocusTrap({
    isOpen: removeAffiliateModalOpen,
    containerRef: removeAffiliateModalRef,
    onClose: closeRemoveAffiliateModal,
    lockScroll: true,
  });

  if (loading) {
    return <div className="page"><p>Loading...</p></div>;
  }
  if (error) {
    return <div className="page"><p className="error-message">{error}</p></div>;
  }

  const filteredLicenses = licenses.filter((lic) => {
    const status = (lic.status || "").toLowerCase();
    const matchesStatus =
      filterStatus === "all" ? true : status === filterStatus;
    const term = searchTerm.trim().toLowerCase();
    if (!matchesStatus) return false;
    if (!term) return true;
    const name = `${lic.profile?.first_name || ""} ${lic.profile?.last_name || ""}`.toLowerCase();
    const number = (lic.license_number || "").toLowerCase();
    const industry = (lic.industry || "").toLowerCase();
    return name.includes(term) || number.includes(term) || industry.includes(term);
  });
  const deleteTargetLicense = licenses.find((lic) => lic.id === deleteLicenseId) || null;
  const selectedAffiliate =
    activeAffiliates.find((affiliate) => affiliate.id === selectedAffiliateId) ||
    affiliateApplications.find((affiliate) => affiliate.id === selectedAffiliateId) ||
    deniedAffiliateApplications.find((affiliate) => affiliate.id === selectedAffiliateId) ||
    activeAffiliates[0] ||
    affiliateApplications[0] ||
    null;
  const removeTargetAffiliate =
    activeAffiliates.find((affiliate) => affiliate.id === removeAffiliateId) ||
    affiliateApplications.find((affiliate) => affiliate.id === removeAffiliateId) ||
    deniedAffiliateApplications.find((affiliate) => affiliate.id === removeAffiliateId) ||
    null;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="eyebrow">Admin</p>
          <h1 data-page-heading="true">License Approvals</h1>
        </div>
      </div>
      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-header">
          <h4>Affiliate Program</h4>
          <button type="button" className="ghost" onClick={fetchAffiliates} disabled={affiliateLoading}>
            {affiliateLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
        {affiliateError && <p className="error-message" style={{ marginTop: 10 }}>{affiliateError}</p>}

        <div className="form-row dual" style={{ marginTop: 10 }}>
          <div>
            <p className="muted" style={{ marginBottom: 8 }}>Pending applications</p>
            <div className="license-list">
              {affiliateApplications.length === 0 && <p className="muted">No pending affiliate applications.</p>}
              {affiliateApplications.map((affiliate) => (
                <div key={affiliate.id} className="license-card admin-card">
                  <div className="license-top">
                    <div>
                      <p className="pill">Pending</p>
                      <h4>{affiliate.full_name}</h4>
                      <p className="muted">{affiliate.email}</p>
                      <p className="muted">{affiliate.audience}</p>
                      {affiliate.notes && <p className="muted" style={{ marginTop: 4 }}>{affiliate.notes}</p>}
                      <p className="muted" style={{ marginTop: 4 }}>Applied: {formatDateTime(affiliate.applied_at || affiliate.created_at)}</p>
                    </div>
                  </div>
                  <div className="button-row" style={{ marginTop: 10 }}>
                    <button type="button" className="ghost" onClick={() => setSelectedAffiliateId(affiliate.id)}>
                      View
                    </button>
                    <button
                      type="button"
                      className="primary dark"
                      onClick={() => updateAffiliateStatus(affiliate.id, "approved")}
                      disabled={affiliateBusyId === affiliate.id}
                    >
                      {affiliateBusyId === affiliate.id ? "Saving..." : "Approve"}
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      onClick={() => updateAffiliateStatus(affiliate.id, "denied")}
                      disabled={affiliateBusyId === affiliate.id}
                    >
                      Deny
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="muted" style={{ marginBottom: 8 }}>Active affiliates</p>
            <div className="license-list">
              {activeAffiliates.length === 0 && <p className="muted">No active affiliates yet.</p>}
              {activeAffiliates.map((affiliate) => (
                <button
                  key={affiliate.id}
                  type="button"
                  className="license-card admin-card"
                  onClick={() => setSelectedAffiliateId(affiliate.id)}
                  style={{ width: "100%", textAlign: "left", cursor: "pointer" }}
                >
                  <div className="license-top">
                    <div>
                      <p className="pill">Active</p>
                      <h4>{affiliate.full_name}</h4>
                      <p className="muted">{affiliate.email}</p>
                      <p className="muted" style={{ wordBreak: "break-all" }}>{affiliate.affiliate_link}</p>
                    </div>
                    <span className="badge success">{affiliate.analytics?.signup_count || 0} signups</span>
                  </div>
                  <p className="muted" style={{ marginTop: 8 }}>
                    {affiliate.analytics?.click_count || 0} link uses - {affiliate.analytics?.active_user_count || 0} active users - {formatCurrency(affiliate.analytics?.estimated_commission_monthly)} est. monthly commission
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label htmlFor="affiliate-decision-notes">Decision notes</label>
          <textarea
            id="affiliate-decision-notes"
            rows="2"
            value={affiliateDecisionNotes}
            onChange={(e) => setAffiliateDecisionNotes(e.target.value)}
            placeholder="Optional internal notes for the next approve or deny action"
          />
        </div>

        {selectedAffiliate && (
          <div className="license-card admin-card" style={{ marginTop: 14 }}>
            <div className="license-top">
              <div>
                <p className="pill">{selectedAffiliate.status}</p>
                <h4>{selectedAffiliate.full_name}</h4>
                <p className="muted">{selectedAffiliate.email}</p>
                {selectedAffiliate.affiliate_link && (
                  <p className="muted" style={{ wordBreak: "break-all" }}>Link: {selectedAffiliate.affiliate_link}</p>
                )}
              </div>
              <span className={`badge ${selectedAffiliate.status === "approved" ? "success" : selectedAffiliate.status === "denied" ? "red" : "muted"}`}>
                {selectedAffiliate.referral_window_days || 60}-day window
              </span>
            </div>
            <div className="button-row" style={{ marginTop: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="ghost"
                onClick={() => openRemoveAffiliateModal(selectedAffiliate.id)}
                disabled={affiliateBusyId === selectedAffiliate.id}
                style={{ color: "#b42318", borderColor: "#f0b3ad" }}
              >
                Remove Affiliate
              </button>
            </div>
            <div className="form-row dual" style={{ marginTop: 12 }}>
              <div>
                <p className="muted">Link uses</p>
                <h4>{selectedAffiliate.analytics?.click_count || 0}</h4>
              </div>
              <div>
                <p className="muted">Referred signups</p>
                <h4>{selectedAffiliate.analytics?.signup_count || 0}</h4>
              </div>
              <div>
                <p className="muted">Active referred users</p>
                <h4>{selectedAffiliate.analytics?.active_user_count || 0}</h4>
              </div>
              <div>
                <p className="muted">Est. monthly commission</p>
                <h4>{formatCurrency(selectedAffiliate.analytics?.estimated_commission_monthly)}</h4>
              </div>
            </div>

            <div style={{ marginTop: 12, overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #e6edf5" }}>Referred user</th>
                    <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #e6edf5" }}>Signup</th>
                    <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #e6edf5" }}>Status</th>
                    <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #e6edf5" }}>Plan</th>
                    <th style={{ textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #e6edf5" }}>Revenue Window</th>
                    <th style={{ textAlign: "right", padding: "10px 8px", borderBottom: "1px solid #e6edf5" }}>Est. Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {(!selectedAffiliate.referrals || selectedAffiliate.referrals.length === 0) && (
                    <tr>
                      <td colSpan="6" style={{ padding: "10px 8px" }} className="muted">No referred users yet.</td>
                    </tr>
                  )}
                  {(selectedAffiliate.referrals || []).map((referral) => (
                    <tr key={referral.id}>
                      <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef3f8" }}>{referral.referred_email || referral.referred_user_id || "-"}</td>
                      <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef3f8" }}>{formatDateTime(referral.signup_at)}</td>
                      <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef3f8" }}>{referral.subscription_status || referral.status || "-"}</td>
                      <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef3f8" }}>
                        {[referral.subscription_tier, referral.subscription_interval].filter(Boolean).join(" / ") || "-"}
                      </td>
                      <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef3f8" }}>
                        {referral.commissionable_until ? `Until ${formatDateTime(referral.commissionable_until)}` : `Referral expires ${formatDateTime(referral.attribution_expires_at)}`}
                      </td>
                      <td style={{ padding: "10px 8px", borderBottom: "1px solid #eef3f8", textAlign: "right" }}>
                        {formatCurrency(referral.estimated_commission_monthly)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-header">
          <h4>Filters</h4>
        </div>
        <div className="form-row dual">
          <div>
            <label htmlFor="admin-filter-status">Status</label>
            <select id="admin-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="issues">Issues</option>
              <option value="all">All</option>
            </select>
          </div>
          <div>
            <label htmlFor="admin-filter-search">Search</label>
            <input
              id="admin-filter-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, license #, industry"
            />
          </div>
        </div>
      </div>

      <div className="panel" style={{ marginBottom: 14 }}>
        <div className="panel-header">
          <h4>Search Exclusions</h4>
          <button
            type="button"
            className="icon-btn"
            aria-label="Open currently excluded list"
            title="Open currently excluded list"
            onClick={() => setExcludedModalOpen(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 5h.01" />
              <path d="M3 12h.01" />
              <path d="M3 19h.01" />
              <path d="M8 5h13" />
              <path d="M8 12h13" />
              <path d="M8 19h13" />
            </svg>
          </button>
        </div>
        <p className="muted" style={{ marginTop: 6 }}>
          Add users here to hide them from public search and Find a Pro results.
        </p>
        <form className="form-row dual" style={{ marginTop: 10 }} onSubmit={handleSearchProfiles}>
          <div>
            <label htmlFor="admin-exclude-search">Find user</label>
            <input
              id="admin-exclude-search"
              type="text"
              value={excludeQuery}
              onChange={(e) => setExcludeQuery(e.target.value)}
              placeholder="Search by name, email, phone, company, or slug"
            />
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
            <button type="submit" className="primary dark" disabled={exclusionsLoading}>
              {exclusionsLoading ? "Searching..." : "Search Users"}
            </button>
            <button
              type="button"
              className="ghost"
              onClick={() => {
                setExcludeQuery("");
                setProfileSearchResults([]);
                setExclusionsError("");
              }}
              disabled={exclusionsLoading}
            >
              Clear
            </button>
          </div>
        </form>

        {exclusionsError && <p className="error-message" style={{ marginTop: 10 }}>{exclusionsError}</p>}

        <div style={{ marginTop: 12 }}>
          <p className="muted" style={{ marginBottom: 8 }}>Search results</p>
          <div className="license-list">
            {!profileSearchResults.length && <p className="muted">Search to find users to add or remove.</p>}
            {profileSearchResults.map((profile) => {
              const isExcluded = profile.exclude_from_search === true;
              return (
                <div key={`search-${profile.id}`} className="license-card admin-card">
                  <div>
                    <h4 style={{ marginBottom: 4 }}>{formatProfileName(profile)}</h4>
                    <p className="muted">{profile.email || "No email"} {profile.phone ? ` - ${profile.phone}` : ""}</p>
                    <p className="muted">{profile.company_agency || "No company"} {profile.public_slug ? ` - /p/${profile.public_slug}` : ""}</p>
                    <p className="muted" style={{ marginTop: 4 }}>
                      Status: {isExcluded ? "Excluded from search" : "Visible in search"}
                    </p>
                  </div>
                  <div className="button-row" style={{ marginTop: 10 }}>
                    <button
                      type="button"
                      className={isExcluded ? "ghost" : "primary dark"}
                      onClick={() => updateExcludedStatus(profile.id, !isExcluded)}
                      disabled={excludeBusyId === profile.id}
                    >
                      {excludeBusyId === profile.id
                        ? "Saving..."
                        : isExcluded
                          ? "Remove from list"
                          : "Exclude from search"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="license-list">
        {filteredLicenses.length === 0 && <p className="muted">No licenses match your filters.</p>}
        {filteredLicenses.map((lic) => {
          const fullName = `${lic.profile?.first_name || ""} ${lic.profile?.last_name || ""}`.trim();
          const nameLine = fullName || "Name unavailable";
          return (
            <div key={lic.id} className="license-card admin-card">
              <div className="license-top">
                <div>
                  <p className="pill">{lic.industry || "License"}</p>
                  <h4>{lic.category || "Category"}</h4>
                  <p className="muted" style={{ marginTop: 4 }}>{nameLine}</p>
                  <p className="muted">License #{lic.license_number || "-"}</p>
                  <p className="muted">
                    State: {lic.state || "-"}  - Issued: {lic.issued_on || "-"}  - Expires: {lic.expires_on || "-"}
                  </p>
                </div>
                <span className={`badge ${lic.status === "active" ? "success" : lic.status === "issues" ? "red" : "muted"}`}>
                  {lic.status || "pending"}
                </span>
              </div>
              <div className="button-row" style={{ marginTop: "0.5rem" }}>
                {lic.docUrl && (
                  <a
                    className="ghost"
                    href={lic.docUrl}
                    target="_blank"
                    rel="noreferrer"
                    style={{ marginRight: "auto" }}
                  >
                    View PDF
                  </a>
                )}
                {!lic.docUrl && lic.supporting_document && (
                  <button
                    className="ghost"
                    type="button"
                    style={{ marginRight: "auto" }}
                    onClick={async () => {
                      try {
                        const storagePath = normalizeStoragePath(lic.supporting_document);
                        if (!storagePath) return;
                        const { data: signedDoc, error: signErr } = await supabase.storage
                          .from("license_docs")
                          .createSignedUrl(storagePath, 3600);
                        if (signErr || !signedDoc?.signedUrl) {
                          alert("License document is missing. Ask the licensee to re-upload the PDF.");
                          return;
                        }
                        window.open(signedDoc.signedUrl, "_blank", "noopener,noreferrer");
                      } catch (err) {
                        alert("License document is missing. Ask the licensee to re-upload the PDF.");
                      }
                    }}
                  >
                    View PDF
                  </button>
                )}
                {!lic.docUrl && !lic.supporting_document && (
                  <span className="muted" style={{ marginRight: "auto" }}>
                    No document on file.
                  </span>
                )}
                {(lic.status || "").toLowerCase() !== "active" && (
                  <button type="button" className="primary dark" onClick={() => updateStatus(lic.id, "active", true)}>Approve</button>
                )}
                <button type="button" className="ghost" onClick={() => handleFlag(lic.id)}>Flag/Reject</button>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => openDeleteModal(lic.id)}
                  style={{ color: "#b42318", borderColor: "#f0b3ad" }}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {flagModalOpen && (
        <div className="modal-backdrop" onClick={() => setFlagModalOpen(false)}>
          <div
            ref={flagModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-flag-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="admin-flag-modal-title">Flag / Reject License</h3>
              <button className="icon-btn" type="button" aria-label="Close flag dialog" onClick={() => setFlagModalOpen(false)}>x</button>
            </div>
            <div className="form-grid">
              <div className="form-row">
                <label htmlFor="admin-flag-reason">Reason</label>
                <select id="admin-flag-reason" value={flagReason} onChange={(e) => setFlagReason(e.target.value)}>
                  <option>Incorrect document</option>
                  <option>Expired document</option>
                  <option>Mismatched details</option>
                  <option>Illegible upload</option>
                  <option>Other</option>
                </select>
              </div>
              {flagReason === "Other" && (
                <div className="form-row">
                  <label htmlFor="admin-flag-notes">Notes</label>
                  <textarea
                    id="admin-flag-notes"
                    rows="3"
                    value={flagNotes}
                    onChange={(e) => setFlagNotes(e.target.value)}
                    placeholder="Add details for the submitter"
                  />
                </div>
              )}
              <div className="button-row" style={{ justifyContent: "flex-end" }}>
                <button className="ghost" type="button" onClick={() => setFlagModalOpen(false)}>Cancel</button>
                <button className="primary dark" type="button" onClick={submitFlag}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteModalOpen && (
        <div className="modal-backdrop" onClick={closeDeleteModal}>
          <div
            ref={deleteModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-delete-license-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="admin-delete-license-modal-title">Delete License</h3>
              <button className="icon-btn" type="button" aria-label="Close delete dialog" onClick={closeDeleteModal}>x</button>
            </div>
            <div className="form-grid">
              <p>
                This will permanently remove this license from the database.
              </p>
              <p className="muted" style={{ marginTop: -6 }}>
                {deleteTargetLicense
                  ? `${deleteTargetLicense.industry || "License"}${deleteTargetLicense.category ? ` - ${deleteTargetLicense.category}` : ""}${deleteTargetLicense.license_number ? ` - #${deleteTargetLicense.license_number}` : ""}`
                  : "The selected license will be deleted."}
              </p>
              <p className="muted">
                Any uploaded supporting document will also be removed. This action cannot be undone.
              </p>
              <div className="button-row" style={{ justifyContent: "flex-end" }}>
                <button className="ghost" type="button" onClick={closeDeleteModal} disabled={deleteBusy}>Cancel</button>
                <button
                  className="primary dark"
                  type="button"
                  onClick={submitDelete}
                  disabled={deleteBusy}
                  style={{ background: "#b42318", borderColor: "#b42318" }}
                >
                  {deleteBusy ? "Deleting..." : "Delete Permanently"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {removeAffiliateModalOpen && (
        <div className="modal-backdrop" onClick={closeRemoveAffiliateModal}>
          <div
            ref={removeAffiliateModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-remove-affiliate-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="admin-remove-affiliate-modal-title">Remove Affiliate</h3>
              <button className="icon-btn" type="button" aria-label="Close remove affiliate dialog" onClick={closeRemoveAffiliateModal}>x</button>
            </div>
            <div className="form-grid">
              <p>
                This will remove the affiliate from the admin affiliate UI and deactivate their affiliate link.
              </p>
              <p className="muted" style={{ marginTop: -6 }}>
                {removeTargetAffiliate
                  ? `${removeTargetAffiliate.full_name || "Affiliate"}${removeTargetAffiliate.email ? ` - ${removeTargetAffiliate.email}` : ""}`
                  : "The selected affiliate will be removed."}
              </p>
              <p className="muted">
                Existing click, signup, and revenue attribution data will stay in the database.
              </p>
              <div className="button-row" style={{ justifyContent: "flex-end" }}>
                <button className="ghost" type="button" onClick={closeRemoveAffiliateModal} disabled={!!affiliateBusyId}>Cancel</button>
                <button
                  className="primary dark"
                  type="button"
                  onClick={submitRemoveAffiliate}
                  disabled={!!affiliateBusyId}
                  style={{ background: "#b42318", borderColor: "#b42318" }}
                >
                  {affiliateBusyId ? "Removing..." : "Remove Affiliate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {excludedModalOpen && (
        <div className="modal-backdrop" onClick={() => setExcludedModalOpen(false)}>
          <div
            ref={excludedModalRef}
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-excluded-modal-title"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 980, width: "min(980px, 96vw)" }}
          >
            <div className="modal-header">
              <h3 id="admin-excluded-modal-title">Currently Excluded From Search</h3>
              <button className="icon-btn" type="button" aria-label="Close excluded list" onClick={() => setExcludedModalOpen(false)}>x</button>
            </div>

            {!excludedProfiles.length ? (
              <p className="muted">No users excluded from search.</p>
            ) : (
              <div style={{ maxHeight: "62vh", overflow: "auto", border: "1px solid #e6edf5", borderRadius: 10 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                  <thead style={{ position: "sticky", top: 0, background: "#f8fbff", zIndex: 1 }}>
                    <tr>
                      <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #e6edf5" }}>Name</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #e6edf5" }}>Email</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #e6edf5" }}>Phone</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #e6edf5" }}>Company</th>
                      <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #e6edf5" }}>Slug</th>
                      <th style={{ textAlign: "right", padding: "10px 12px", borderBottom: "1px solid #e6edf5" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {excludedProfiles.map((profile) => (
                      <tr key={`excluded-row-${profile.id}`}>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef3f8" }}>{formatProfileName(profile)}</td>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef3f8" }}>{profile.email || "-"}</td>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef3f8" }}>{profile.phone || "-"}</td>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef3f8" }}>{profile.company_agency || "-"}</td>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef3f8" }}>{profile.public_slug ? `/p/${profile.public_slug}` : "-"}</td>
                        <td style={{ padding: "10px 12px", borderBottom: "1px solid #eef3f8", textAlign: "right" }}>
                          <button
                            type="button"
                            className="ghost"
                            onClick={() => updateExcludedStatus(profile.id, false)}
                            disabled={excludeBusyId === profile.id}
                          >
                            {excludeBusyId === profile.id ? "Saving..." : "Remove"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


